import React, { useEffect, useMemo, useState } from "react";
import Countdown from "react-countdown";
import { Link } from "react-router-dom";
import { DEFAULT_PROMO, daysUntil, fillPromoParts, fillPromoText, getPromoEvent } from "./promoEvent";

const pad = (v) => (v > 9 ? v : `0${v}`);

// fixedNow: mốc giờ giả lập (xem trước trong Dashboard) — đồng hồ đứng yên tại mốc đó
const Clock = ({ date, onDone, fixedNow }) => (
  <Countdown
    date={date}
    onComplete={onDone}
    {...(fixedNow ? { now: () => fixedNow.getTime(), controlled: false, autoStart: false } : {})}
    renderer={({ days, hours, minutes, seconds }) => (
      <span className="flex items-center gap-1 tabular-nums" role="timer">
        {[days * 24 + hours, minutes, seconds].map((v, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="font-bold text-white/70">:</span>}
            <span className="inline-flex min-w-[26px] justify-center rounded-md bg-slate-900/70 px-1 py-0.5 text-sm font-bold text-white lg:min-w-[30px] lg:text-base">
              {pad(v)}
            </span>
          </React.Fragment>
        ))}
      </span>
    )}
  />
);

/**
 * Thanh khuyến mãi đầu trang — dựng bằng code, KHÔNG cần thiết kế ảnh mỗi tháng.
 * Tự lấy đợt sale kế tiếp trong lịch: đầu tháng (1), ngày đôi (m.m), giữa tháng (15), lương về (25).
 * Còn xa → "Còn N ngày"; dưới 24 giờ → đếm ngược; đúng ngày → nền đỏ "Đang diễn ra"; qua đợt → đợt kế.
 * config: bản ghi banner name="promo_bar" (Dashboard → Banner & hình ảnh → Thanh khuyến mãi).
 */
const PromoBar = ({ config, preview = false, now: fixedNow }) => {
  const [tick, setTick] = useState(0); // tăng khi hết giờ đếm ngược / mỗi phút → tính lại đợt sale
  const cta = config?.cta || DEFAULT_PROMO.cta;
  const link = config?.link || DEFAULT_PROMO.link;
  const eventsKey = JSON.stringify(config?.events || null);

  const event = useMemo(
    () => getPromoEvent(fixedNow || new Date(), config?.events),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [tick, fixedNow, eventsKey]
  );

  // Mở trang lâu qua nửa đêm → số ngày còn lại / đợt sale đổi → tính lại mỗi phút
  useEffect(() => {
    if (preview || fixedNow) return undefined;
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, [preview, fixedNow]);

  if (!event) return null; // admin tắt hết các đợt

  const now = fixedNow || new Date();
  const days = daysUntil(event.start, now);
  const soon = !event.live && event.start - now <= 86400000;
  const titleParts = fillPromoParts(event.title, event);
  const title = fillPromoText(event.title, event);
  const description = fillPromoText(event.description, event);
  const bg = event.live
    ? "bg-gradient-to-r from-sale-700 via-sale-600 to-orange-500"
    : "bg-gradient-to-r from-brand-950 via-brand-800 to-brand-700";
  const refresh = () => setTick((t) => t + 1);

  const status = event.live ? (
    <span className="flex items-center gap-2">
      <span className="hidden items-center gap-1.5 text-sm font-semibold text-white sm:flex">
        <span className="h-2 w-2 animate-pulse rounded-full bg-amber-300" aria-hidden="true" />
        Đang diễn ra · kết thúc sau
      </span>
      <Clock date={event.end} fixedNow={fixedNow} onDone={refresh} />
    </span>
  ) : soon ? (
    <span className="flex items-center gap-2">
      <span className="hidden text-sm font-medium text-white/85 sm:inline">Bắt đầu sau</span>
      <Clock date={event.start} fixedNow={fixedNow} onDone={refresh} />
    </span>
  ) : (
    <span className="rounded-full bg-white/15 px-2.5 py-0.5 text-sm font-semibold text-white ring-1 ring-white/20">
      {days === 1 ? "Ngày mai" : `Còn ${days} ngày`}
    </span>
  );

  const inner = (
    <div className="container-hm flex h-9 items-center gap-3 lg:h-11 lg:gap-5">
      <span className="flex min-w-0 items-center gap-2">
        <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-amber-400 text-sm text-slate-900 lg:h-7 lg:w-7">
          <i className="bi bi-lightning-charge-fill" aria-hidden="true" />
        </span>
        {/* Không dùng `truncate` (overflow:hidden cả 2 chiều): cắt mất dấu chồng chữ hoa tiếng Việt (Ề, Ố…)
            ở trên và phần nghiêng của chữ italic ở bên phải. Chỉ cắt chiều ngang + chừa lề phải cho chữ nghiêng. */}
        <span className="min-w-0 overflow-x-clip overflow-y-visible text-ellipsis whitespace-nowrap py-1 pr-1.5 text-base font-extrabold italic leading-normal tracking-tight text-white lg:text-xl [text-shadow:0_1px_0_rgba(0,0,0,0.25)]">
          {titleParts.map((p, i) => (p.hl ? <span key={i} className="text-amber-300">{p.text}</span> : <React.Fragment key={i}>{p.text}</React.Fragment>))}
        </span>
      </span>

      {description && <span className="hidden min-w-0 truncate text-base font-medium text-white/85 lg:block">{description}</span>}

      <span className="ml-auto flex flex-shrink-0 items-center gap-2 lg:gap-3">
        {status}
        <span className="hidden items-center gap-1 rounded-full bg-amber-400 px-3.5 py-1 text-sm font-bold text-slate-900 transition-colors group-hover:bg-amber-300 sm:inline-flex lg:text-base">
          {cta}
          <i className="bi bi-chevron-right text-xs" aria-hidden="true" />
        </span>
        <i className="bi bi-chevron-right text-sm text-white/80 sm:hidden" aria-hidden="true" />
      </span>
    </div>
  );

  const cls = `group relative block overflow-hidden no-underline ${bg}`;
  // Hoạ tiết chấm sáng nhẹ (thuần CSS) cho cảm giác "sale" mà không cần ảnh
  const body = (
    <>
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-25"
        style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.45) 1px, transparent 1.5px)", backgroundSize: "18px 18px" }}
      />
      <div className="relative">{inner}</div>
    </>
  );
  const label = `${title}${description ? ` — ${description}` : ""}`;

  if (preview) {
    return (
      <div className={cls} aria-label={`Xem trước: ${label}`}>
        {body}
      </div>
    );
  }
  // Link ngoài (https://…) → thẻ <a>; đường dẫn nội bộ (/flash-sale) → Link (không tải lại trang)
  if (/^https?:\/\//i.test(link)) {
    return (
      <a href={link} target="_blank" rel="noreferrer" className={cls} aria-label={label}>
        {body}
      </a>
    );
  }
  return (
    <Link to={link} className={cls} aria-label={label}>
      {body}
    </Link>
  );
};

export default PromoBar;
